const BaseInfo = `
  id,
  name
`;

const ClassifierTargetLabelInfo = `
  targetLabel {
    ${BaseInfo}
  }
`;
// precision
const ClassifierTargetsInfo = `
  clfTargets {
    clfTargetId: id,
    ${ClassifierTargetLabelInfo}
  }
`;

const ClassifierDetailInfo = `
  ${BaseInfo},
  coverage,
  type,
  enabledSince,
  ${ClassifierTargetsInfo}
`;

const getClassifiers = (name) => {
  return {
    query: `{
      classifiers: abstractClassifiers (first: 60, like: {name: "%${name}%"}) {
        ${BaseInfo}
      }
    }`
  };
}

const getClassifier = (classifierId) => {
  return {
    query: `{
      classifierDetail: abstractClassifier (id: ${classifierId}) {
        ${ClassifierDetailInfo}
      }
    }`
  };
}

const getWeightedClfTargets = (classifierId, clfTargetId) => {
  return {
    query: `{
      clfTarget: abstractClassifier(id: ${classifierId}) {
        id,
        clfTarget(id: ${clfTargetId}) {
          id,
          weightedClfTargets {
            labelId,
            label {
              labelType,
              name
            },
            weight
          }
        }
      }
    }`
  };
}

const MutationWeightedClfTargetType = (mutationType, clfTargetId, labelId, weight) => {
  if (mutationType === 'delete') {
    return `savedWeightedTarget: deleteWeightedClfTarget (
      labelId: ${labelId},
      clfTargetId: ${clfTargetId}
    )`;
  } else {
    return `savedWeightedTarget: ${mutationType}WeightedClfTarget (
      labelId: ${labelId},
      clfTargetId: ${clfTargetId},
      weight: ${weight}
    )`;
  }
}

const MutationQueryResult = (mutationType) => {
  if (mutationType === 'delete') {
    return `okay`;
  } else {
    return `weightedClfTarget {
      labelId,
      clfTargetId,
      weight
    }`;
  }
};

const getMutationWeightedClfTargetQuery = (mutationType, clfTargetId, labelId, weight) => {
  return {
    query: `
      mutation {
        ${MutationWeightedClfTargetType(mutationType, clfTargetId, labelId, weight)} {
          ${MutationQueryResult(mutationType)}
        }
      }
    `
  }
};

const getTextDetectorResults = (clfTargetId, lastTextDetectorId) => {
  const queryArgs = !!lastTextDetectorId ? `clfTargetId: ${clfTargetId}, first:10, after:"${lastTextDetectorId}", order:"pageId"` : `clfTargetId: ${clfTargetId}, first:20`;
  return {
    query: `{
      textDetectorResults: textDetectorResults (${queryArgs}) {
        clfTargetId,
        pageId,
        webPage{
          title,
          domain
        }
      }
    }`
  };
}

const getBoxDetectorResults = (clfTargetId) => {
  return {
    query: `{
      boxDetectorResults: boxDetectorResults (clfTargetId: ${clfTargetId}, first:100) {
        clfTargetId,
        boxId,
        box {
          boxType,
          id,
          x,
          y,
          width,
          height,
          timestamp,
          videoId
        }
      }
    }`
  };
}

const getVideoDetectorResults = (clfTargetId) => {
  return {
    query: `{
      videoDetectorResults: videoDetectorResults (clfTargetId: ${clfTargetId}, first:135) {
        clfTargetId,
        videoId,
        video {
          id,
          length,
          s3Images,
          page {
            id,
            domain,
            title,
            remoteId
          }
        }
      }
    }`
  };
}

const hitEvaluatorOpt = {
  'Page': {
    queryEntity: 'webPageTextEvaluator',
    hitEnabled:  'pageQaEnabled',
    createEntity: 'createWebPageTextEvaluator',
    updateEntity: 'updateWebPageTextEvaluator'
  },
  'Video': {
    queryEntity: 'videoCollageEvaluator',
    hitEnabled:  'videoQaEnabled',
    createEntity: 'createVideoCollageEvaluator',
    updateEntity: 'updateVideoCollageEvaluator'
  },
  'Image': {
    queryEntity: 'clickableImageEvaluator',
    hitEnabled:  'imageQaEnabled',
    createEntity: 'createClickableImageEvaluator',
    updateEntity: 'updateClickableImageEvaluator'
  },
  'Box': {
    queryEntity: 'clickableBoxEvaluator',
    hitEnabled:  'boxQaEnabled',
    createEntity: 'createClickableBoxEvaluator',
    updateEntity: 'updateClickableBoxEvaluator'
  }
}

const hitEvaluatorInfo = (hitEnabled) => {
  return `
    id,
    title,
    description,
    question,
    targetLabelId,
    targetLabel {
      targetClassifier {
        hitEnabled: ${hitEnabled}
      }
    }
  `;
}

const getMTurkEvaluator = (entityName, targetLabelId) => {
  const { queryEntity, hitEnabled } = hitEvaluatorOpt[entityName];
  return {
    query: `{
      mturkEvaluator: ${queryEntity} (targetLabelId: ${targetLabelId}) {
        ${hitEvaluatorInfo(hitEnabled)}
      }
    }`
  };
}

const clfTargetMturkQaEnabledInfo = `
  id,
  videoQaEnabled,
  pageQaEnabled,
  boxQaEnabled,
  imageQaEnabled
`;

const getClfTargetMturkQaEnabled = (clfTargetId) => {
  return {
    query: `{
      turkQaEnabled: classifierTarget(id: ${clfTargetId}) {
        ${clfTargetMturkQaEnabledInfo}
      }
    }`
  };
}

const getMutationMTurkEvaluator = (entityName, mturkEvaluator, clfTargetId) => {
  const evaluatorOpt = hitEvaluatorOpt[entityName];
  const mutationEntityName = !!mturkEvaluator.id ? evaluatorOpt.updateEntity : evaluatorOpt.createEntity;
  const mutationId = !!mturkEvaluator.id ? `id: ${mturkEvaluator.id}` : `targetLabelId: ${mturkEvaluator.targetLabelId}, name: \"${mturkEvaluator.title}\"`;

  return {
    query: `
      mutation {
        savedTurkQaEnabled: updateClassifierTarget (
          id: ${clfTargetId},
          ${evaluatorOpt.hitEnabled}: ${mturkEvaluator.targetLabel.targetClassifier.hitEnabled}
        ) {
          classifierTarget {
            ${clfTargetMturkQaEnabledInfo}
          }
        },
        savedMTurkEvaluator: ${mutationEntityName} (
          ${mutationId},
          title: \"${mturkEvaluator.title}\",
          description: \"${mturkEvaluator.description}\",
          question: \"${mturkEvaluator.question}\"
        ) {
          mturkEvaluator: ${evaluatorOpt.queryEntity} {
            ${hitEvaluatorInfo(evaluatorOpt.hitEnabled)}
          }
        }
      }
    `
  }
}

export default {
  getClassifiers,
  getClassifier,
  getWeightedClfTargets,
  getMutationWeightedClfTargetQuery,
  getTextDetectorResults,
  getBoxDetectorResults,
  getVideoDetectorResults,
  getClfTargetMturkQaEnabled,
  getMTurkEvaluator,
  getMutationMTurkEvaluator
}



// WEBPACK FOOTER //
// ./src/components/classifier/classifier.query.jsx